// src/stores/auth.js
import { defineStore } from "pinia";
import axios from "@/axios";
import router from "@/router";
import { APP_VERSION, VERSION_STORAGE_KEY } from "@/constants";

const safeJsonParse = (item) => {
  try {
    const value = localStorage.getItem(item);
    if (value === null || value === undefined || value === 'undefined') {
      return null;
    }
    return JSON.parse(value);
  } catch (error) {
    console.warn(`Error parsing ${item} from localStorage:`, error);
    localStorage.removeItem(item);
    return null;
  }
};

export const useAuthStore = defineStore("auth", {
  state: () => ({
    user: safeJsonParse("user"),
    token: localStorage.getItem("token") || null,
    loading: false,
    error: null,
    tokenExpiresAt: localStorage.getItem("tokenExpiresAt") || null,
  }),

  getters: {
    isAuthenticated: (state) => {
      // Verificar si el token existe y no ha expirado
      if (!state.token) return false;
      
      // Si no hay fecha de expiración establecida, verificar solo el token y usuario
      if (!state.tokenExpiresAt) return !!state.token && !!state.user;
      
      // Verificar si el token ha expirado
      const now = new Date().getTime();
      const expiresAt = parseInt(state.tokenExpiresAt);
      return now < expiresAt && !!state.user;
    },
    isAdmin: (state) => state.user?.role === "admin",
    getUser: (state) => state.user,
    getToken: (state) => state.token,
  },

  actions: {
    setToken(token) {
      this.token = token;
      localStorage.setItem("token", token);
      
      // Establecer fecha de expiración (30 días)
      const expiresAt = new Date().getTime() + (30 * 24 * 60 * 60 * 1000);
      this.tokenExpiresAt = expiresAt.toString();
      localStorage.setItem("tokenExpiresAt", expiresAt.toString());
      
      // Configurar token en axios
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    },

    setUser(user) {
      this.user = user;
      try {
        if (user === null || user === undefined) {
          localStorage.removeItem("user");
        } else {
          localStorage.setItem("user", JSON.stringify(user));
        }
      } catch (error) {
        console.warn("Error saving user to localStorage:", error);
        // Si falla el guardado, al menos limpiar el valor corrupto
        localStorage.removeItem("user");
      }
    },

    async login(credentials) {
      this.loading = true;
      this.error = null;

      try {
        const response = await axios.post("/v1/login", credentials);
        
        if (response.data && response.data.token) {
          this.setToken(response.data.token);
          this.setUser(response.data.user);
          return response;
        } else {
          throw new Error("No se recibió un token válido");
        }
      } catch (error) {
        this.error = error.response?.data?.message || "Error al iniciar sesión";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async register(userData) {
      this.loading = true;
      this.error = null;

      try {
        const response = await axios.post("/auth/register", userData);
        this.setToken(response.data.token);
        this.setUser(response.data.user);
        return response;
      } catch (error) {
        this.error = error.response?.data?.message || "Error al registrarse";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async fetchUserData() {
      if (!this.token) return null;

      this.loading = true;

      try {
        const response = await axios.get("/v1/profile");
        this.setUser(response.data.user);
        console.log('response.data.user', response.data.user);
        return response.data;
      } catch (error) {
        console.error("Error al obtener datos del usuario:", error);

        // Si el token es inválido, cerrar sesión
        if (error.response?.status === 401) {
          this.logout();
        }

        this.error =
          error.response?.data?.message || "Error al obtener datos del usuario";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async updateProfile(userData) {
      this.loading = true;

      try {
        // Asegurarse de que todos los campos necesarios estén incluidos
        const profileData = {
          name: userData.name,
          phone: userData.phone,
          country_code: userData.country_code,
          birthdate: userData.birthdate,
          shirt_size: userData.shirt_size
        };

        const response = await axios.post("/v1/update-profile", profileData);
        
        // Actualizar el usuario en el store y localStorage
        if (response.data.user) {
          this.setUser({
            ...this.user,
            ...response.data.user
          });
        }
        
        return response.data;
      } catch (error) {
        this.error = error.response?.data?.message || "Error al actualizar perfil";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async updateCountry(countryId) {
      this.loading = true;

      try {
        const response = await axios.post("/v1/update-country", {
          country_id: countryId
        });
        this.setUser(response.data.user);
        return response.data;
      } catch (error) {
        this.error =
          error.response?.data?.message || "Error al actualizar país";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async changePassword(passwordData) {
      this.loading = true;

      try {
        const response = await axios.put("/auth/change-password", passwordData);
        return response;
      } catch (error) {
        this.error =
          error.response?.data?.message || "Error al cambiar contraseña";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async logout() {
      try {
        // Opcionalmente, llama al endpoint de logout en el backend
        if (this.token) {
          await axios.post("/v1/logout").catch(() => {
            // Silenciar errores del logout en el backend
          });
        }
      } finally {
        // Limpiar datos locales independientemente de la respuesta del servidor
        this.token = null;
        this.user = null;
        this.error = null;
        this.tokenExpiresAt = null;
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("tokenExpiresAt");
        delete axios.defaults.headers.common["Authorization"];

        // Redirigir al login
        router.push("/login");
      }
    },

    async forgotPassword(email) {
      this.loading = true;

      try {
        const response = await axios.post("/auth/forgot-password", { email });
        return response;
      } catch (error) {
        this.error =
          error.response?.data?.message ||
          "Error al solicitar recuperación de contraseña";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async resetPassword(resetData) {
      this.loading = true;

      try {
        const response = await axios.post("/auth/reset-password", resetData);
        return response;
      } catch (error) {
        this.error =
          error.response?.data?.message || "Error al restablecer contraseña";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // Verificar token al iniciar la aplicación
    async checkAuth() {
      if (!this.token) return false;

      // Si ya está autenticado pero no tiene datos de usuario
      if (this.isAuthenticated && !this.user) {
        try {
          await this.fetchUserData();
        } catch (error) {
          return false;
        }
      }
      
      return this.isAuthenticated;
    },

    // Verificar si el usuario ya está autenticado y redirigir si es necesario
    async redirectIfAuthenticated(redirectPath = '/dashboard') {
      if (await this.checkAuth()) {
        router.replace(redirectPath);
        return true;
      }
      return false;
    },

    // Método para manejar la autenticación social
    async handleSocialLogin(socialData) {
      this.loading = true;
      this.error = null;
    
      try {
        const response = await axios.post("/v1/google-login", socialData);
        
        if (response.data && response.data.token) {
          this.setToken(response.data.token);
          this.setUser(response.data.user);
          return response.data;
        } else {
          throw new Error("No se recibió un token válido");
        }
      } catch (error) {
        this.error = error.response?.data?.message || "Error en la autenticación social";
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // Limpiar errores
    clearError() {
      this.error = null;
    },
    
    // Configurar token en axios (útil al iniciar la aplicación)
    setupAxiosToken() {
      if (this.token) {
        axios.defaults.headers.common["Authorization"] = `Bearer ${this.token}`;
      }
    },

    validateAppVersion() {
      const storedVersion = localStorage.getItem(VERSION_STORAGE_KEY);
      const currentVersion = APP_VERSION;
      
      
      if (storedVersion !== currentVersion) {
        console.warn(`Versión desactualizada detectada. Limpiando localStorage...`);
        this.clearAllStorage();
        
        localStorage.setItem(VERSION_STORAGE_KEY, currentVersion);
        
        return false;
      }
      
      return true;
    },

    clearAllStorage() {
      const itemsToClear = ['user', 'token', 'tokenExpiresAt'];
      
      itemsToClear.forEach(item => {
        localStorage.removeItem(item);
      });
      
      this.user = null;
      this.token = null;
      this.error = null;
      this.tokenExpiresAt = null;
      
      delete axios.defaults.headers.common["Authorization"];
      
      console.log('LocalStorage limpiado por actualización de versión');
    },

    clearCorruptedStorage() {
      const itemsToCheck = ['user', 'token', 'tokenExpiresAt'];
      
      itemsToCheck.forEach(item => {
        try {
          const value = localStorage.getItem(item);
          if (value === 'undefined' || (value === 'null' && item === 'user')) {
            console.warn(`Removing corrupted ${item} from localStorage`);
            localStorage.removeItem(item);
          }
          if (item === 'user' && value && value !== 'null') {
            JSON.parse(value);
          }
        } catch (error) {
          console.warn(`Removing corrupted ${item} from localStorage:`, error);
          localStorage.removeItem(item);
        }
      });
    },

    initializeStore() {
      const isValidVersion = this.validateAppVersion();
      
      if (isValidVersion) {
        this.clearCorruptedStorage();
        this.setupAxiosToken();
      } else {
        console.log('Se detectó una nueva versión. Por favor, inicia sesión nuevamente.');
      }
    }
  },
});